import { PlatformPrivateEvent, PlatformAppEvent, PlatformLifecycleEvent, PlatformAppEventEmitter } from '@wix/public-editor-platform-events';
import { EnvironmentContext, ApplicationContext, PlatformEnvironment, APPLICATION_CONTEXT_KEY } from '@wix/editor-platform-contexts';
import { createErrorBuilder, BaseError, createEditorPlatformInternalError, EditorPlatformInternalErrorCode } from '@wix/public-editor-platform-errors';
import { EventType } from '@wix/public-editor-platform-interfaces';
import { WorkerConsumerEndpoint, IFrameConsumerEndpoint, WorkerHostEndpoint } from '@wix/editor-platform-transport';

class ApplicationEventsBridge {
  constructor(platformAppEvents) {
    this.platformAppEvents = platformAppEvents;
  }
  /**
   * Notify by event from Worker Manager (platform infrastructure)
   */
  notify(event) {
    switch (event.type) {
      case PlatformLifecycleEvent.EditorReady:
        this.platformAppEvents.notify({
          ...event,
          // @ts-expect-error TODO: fix me
          type: PlatformAppEvent.EditorReady
        });
        break;
      case PlatformPrivateEvent.HostEvent:
        this.platformAppEvents.notify({
          ...event,
          type: PlatformAppEvent.HostEvent
        });
        break;
    }
  }
  /**
   * Subscribe to Worker (Application) event
   */
  subscribe(cb) {
    this.platformAppEvents.subscribe((event) => {
      cb(event);
    });
  }
}

var PlatformConsumerEnvironmentAPIType = /* @__PURE__ */ ((PlatformConsumerEnvironmentAPIType2) => {
  PlatformConsumerEnvironmentAPIType2["Frame"] = "PLATFORM_FRAME_API";
  PlatformConsumerEnvironmentAPIType2["Worker"] = "PLATFORM_WORKER_API";
  return PlatformConsumerEnvironmentAPIType2;
})(PlatformConsumerEnvironmentAPIType || {});
class AbstractEnvironmentAPI {
  constructor(type, envType) {
    this.type = type;
    this.envType = envType;
  }
  async injectEnvironmentContext({
    events,
    privateApi
  }) {
    await EnvironmentContext.inject({
      environment: this.envType,
      events,
      privateApi,
      applicationAPIs: {}
    });
  }
  async injectApplicationContext(context) {
    await ApplicationContext.inject(context);
  }
}

const DESIGN_SYSTEM_STYLES_MAP = {
  classic: "https://www.unpkg.com/@wix/design-system/dist/statics/tokens-default.global.css",
  studio: "https://www.unpkg.com/@wix/design-system/dist/statics/tokens-studio.global.css"
};
class PlatformFrameAPI extends AbstractEnvironmentAPI {
  #events = new PlatformAppEventEmitter();
  #eventsBridge = new ApplicationEventsBridge(this.#events);
  #privateAPI;
  #applicationPrivateAPI;
  constructor() {
    super(PlatformConsumerEnvironmentAPIType.Frame, PlatformEnvironment.Frame);
    this.#injectGlobalCSSTokens();
  }
  #injectGlobalCSSTokens() {
    if (typeof globalThis?.document?.head?.prepend === "function") {
      const params = new URL(globalThis.location.href).searchParams;
      const host = params.get("editorType") === "CLASSIC" ? "classic" : "studio";
      const url = DESIGN_SYSTEM_STYLES_MAP[host];
      const isAlreadyLoaded = url && !!document.querySelectorAll(`link[type="text/css"][href="${url}"]`)?.length;
      if (url && !isAlreadyLoaded) {
        const link = document.createElement("link");
        link.setAttribute("rel", "stylesheet");
        link.setAttribute("type", "text/css");
        link.setAttribute("href", url);
        globalThis.document.head.prepend(link);
      }
    }
  }
  /**
   *
   * We need to introduce context versions
   * to simplify handling the backward compatibility issues
   */
  async initEnvironment(props) {
    const { applicationPrivateAPI, privateAPI, context } = props;
    const _appDefinitionId = props.context?.appDefinitionId ?? props.appDefinitionId;
    const _context = props.context ? props.context : {
      appDefinitionId: _appDefinitionId
    };
    this.#applicationPrivateAPI = applicationPrivateAPI;
    this.#privateAPI = privateAPI;
    await this.injectEnvironmentContext({
      privateApi: privateAPI,
      events: this.#events,
      applicationAPIs: _appDefinitionId ? {
        [_appDefinitionId]: this.#applicationPrivateAPI
      } : {}
    });
    await this.injectApplicationContext(_context);
  }
  notify(event) {
    this.#eventsBridge.notify(event);
  }
  subscribe(cb) {
    this.#eventsBridge.subscribe(cb);
  }
}

var EditorPlatformApplicationContainerErrorCode = /* @__PURE__ */ ((EditorPlatformApplicationContainerErrorCode2) => {
  EditorPlatformApplicationContainerErrorCode2["ApplicationLoadError"] = "ApplicationLoadError";
  EditorPlatformApplicationContainerErrorCode2["ApplicationFetchError"] = "ApplicationFetchError";
  EditorPlatformApplicationContainerErrorCode2["ApplicationExecuteError"] = "ApplicationExecuteError";
  return EditorPlatformApplicationContainerErrorCode2;
})(EditorPlatformApplicationContainerErrorCode || {});
class EditorPlatformApplicationContainerError extends BaseError {
  state = {};
  constructor(message, code) {
    super(message, code, "EP Application Container Error");
  }
  withUrl(url) {
    this.state = { ...this.state, url };
    return this;
  }
  withAppDefinitionId(appDefinitionId) {
    this.state = { ...this.state, appDefinitionId };
    return this;
  }
  withApiMethod(apiMethod) {
    this.state = { ...this.state, apiMethod };
    return this;
  }
  withApiType(apiType) {
    this.state = { ...this.state, apiType };
    return this;
  }
}
const createEditorPlatformApplicationContainerError = createErrorBuilder(EditorPlatformApplicationContainerError);

const APPLICATION_REGISTRY_KEY = "__APPLICATION_REGISTRY_KEY";
async function executeApplication(events, spec, bundle) {
  const executable = new Function(
    APPLICATION_CONTEXT_KEY,
    APPLICATION_REGISTRY_KEY,
    bundle
  );
  let instance;
  const applicationRegistryCallback = (_instance) => {
    if (instance) {
      throw createEditorPlatformApplicationContainerError(
        EditorPlatformApplicationContainerErrorCode.ApplicationExecuteError,
        "Application registry called more than once"
      ).withAppDefinitionId(spec.appDefinitionId);
    }
    if (_instance.type !== spec.type) {
      throw createEditorPlatformApplicationContainerError(
        EditorPlatformApplicationContainerErrorCode.ApplicationExecuteError,
        "Application has different type"
      ).withMessage("expected type", spec.type).withMessage("received type", _instance.type);
    }
    instance = _instance;
  };
  try {
    const context = { ...spec };
    executable.call(
      null,
      new ApplicationContext(context, await EnvironmentContext.getInstance()),
      applicationRegistryCallback
    );
  } catch (e) {
    throw createEditorPlatformApplicationContainerError(
      EditorPlatformApplicationContainerErrorCode.ApplicationExecuteError,
      e.message
    ).withAppDefinitionId(spec.appDefinitionId).withParentError(e);
  }
  return new Promise((resolve, reject) => {
    const unsubscribe = events.subscribe((event) => {
      const timeoutId = setTimeout(() => {
        clearTimeout(timeoutId);
        unsubscribe();
        if (!instance) {
          reject(
            createEditorPlatformApplicationContainerError(
              EditorPlatformApplicationContainerErrorCode.ApplicationExecuteError,
              "Application registry was not called, threw by timeout"
            ).withAppDefinitionId(spec.appDefinitionId)
          );
        }
      }, 5e3);
      if (event.type === PlatformAppEvent.ApplicationInit && event.meta.appDefinitionId === spec.appDefinitionId) {
        clearTimeout(timeoutId);
        unsubscribe();
        if (!instance) {
          reject(
            createEditorPlatformApplicationContainerError(
              EditorPlatformApplicationContainerErrorCode.ApplicationExecuteError,
              "Application registry was not called"
            ).withAppDefinitionId(spec.appDefinitionId)
          );
        }
        resolve({ instance });
      }
    });
  });
}

class PlatformApplicationContainer {
  #apps = {};
  #privateAPI;
  #events;
  constructor(privateApi, events) {
    this.#privateAPI = privateApi;
    this.#events = events;
    this.#events.subscribe((event) => {
      switch (event.type) {
        case PlatformAppEvent.HostEvent: {
          if (event.payload.type === EventType.removeAppCompleted) {
            void this.#events.withEvent(
              this.#events.factories.createApplicationRemovedEvent(
                event.payload.appDefinitionId
              ),
              () => {
                return this.removeApplication(event.payload.appDefinitionId);
              }
            );
          }
          break;
        }
      }
    });
  }
  async runApplication(appSpec) {
    const bundle = await this.loadApplication(appSpec);
    const instance = await this.executeApplication(appSpec, bundle);
    this.setApplication(appSpec.appDefinitionId, instance);
    return instance;
  }
  setApplication(appDefId, instance) {
    this.#apps[appDefId] = instance;
    void this.#events.withEvent(
      this.#events.factories.createApplicationApiInitEvent(
        appDefId,
        // TODO: both types are set here...
        // @ts-expect-error TODO: fix me
        instance?.api?.private ? "private" : "public"
      ),
      () => {
        this.#privateAPI.applicationManager.setApplication(instance);
      }
    );
  }
  getApplication(appDefId) {
    return this.#apps[appDefId];
  }
  getAppDefinitionIds() {
    return Object.keys(this.#apps);
  }
  removeApplication(appDefinitionId) {
    delete this.#apps[appDefinitionId];
  }
  async loadApplication(appSpec) {
    const url = appSpec.url;
    return this.#events.withEvent(
      this.#events.factories.createApplicationLoadEvent(appSpec, url),
      async () => {
        try {
          return await this.loadApplicationBundle(url);
        } catch (e) {
          throw createEditorPlatformApplicationContainerError(
            EditorPlatformApplicationContainerErrorCode.ApplicationLoadError
          ).withUrl(url).withAppDefinitionId(appSpec.appDefinitionId).withParentError(e);
        }
      }
    );
  }
  async loadApplicationBundle(url) {
    const res = await fetch(url, {
      method: "GET"
    });
    const isSuccessfulResponse = res.status >= 200 && res.status <= 299;
    if (!isSuccessfulResponse) {
      throw createEditorPlatformApplicationContainerError(
        EditorPlatformApplicationContainerErrorCode.ApplicationFetchError
      ).withUrl(url);
    }
    return res.text();
  }
  async executeApplication(appSpec, bundle) {
    return this.#events.withEvent(
      this.#events.factories.createApplicationExecuteEvent(
        appSpec,
        appSpec.url
      ),
      async () => {
        const { instance } = await executeApplication(
          this.#events,
          appSpec,
          bundle
        );
        return instance;
      }
    );
  }
}

class PlatformWorkerAPI extends AbstractEnvironmentAPI {
  #events = new PlatformAppEventEmitter();
  #eventsBridge = new ApplicationEventsBridge(this.#events);
  #container = null;
  #privateAPI;
  #pendingWaiters = [];
  constructor() {
    super(
      PlatformConsumerEnvironmentAPIType.Worker,
      PlatformEnvironment.Worker
    );
  }
  create() {
  }
  async initEnvironment(props) {
    const { buildPrivateAPI } = props;
    this.#privateAPI = await buildPrivateAPI({
      // TODO: should be per application (within the container before app execution)
      type: "EDITOR_ADDON"
    });
    await this.injectEnvironmentContext({
      events: this.#events,
      privateApi: this.#privateAPI,
      applicationAPIs: {}
    });
    this.#container = new PlatformApplicationContainer(
      this.#privateAPI,
      this.#events
    );
    this.#pendingWaiters.forEach((res) => res(this));
  }
  async notify(event) {
    await this.waitReady();
    this.#eventsBridge.notify(event);
  }
  subscribe(cb) {
    this.#eventsBridge.subscribe(cb);
  }
  async runApplication(app) {
    await this.waitReady();
    await this.#container.runApplication(app);
  }
  // TODO: should not be any waiters here
  // or should be implemented inside the events instances
  // collect queue until app is not ready
  waitReady() {
    return new Promise((res) => {
      if (this.#privateAPI) {
        return res(this);
      }
      this.#pendingWaiters.push(res);
    });
  }
}

const GUARD_PROP = "$_EP_TRANSPORT_ENV_CONTEXT_GUARD";
class EditorPlatformContextEnvironment {
  isEnvironmentApiExposed() {
    return !!globalThis[GUARD_PROP];
  }
  async getContexts() {
    const [environmentContext, applicationContext] = await Promise.all([
      EnvironmentContext.getInstance(),
      ApplicationContext.getInstance()
    ]);
    return {
      environmentContext,
      applicationContext
    };
  }
  expose() {
    if (this.isEnvironmentApiExposed()) {
      return;
    }
    const isWorker = typeof importScripts === "function";
    const isIframe = globalThis.self && globalThis.top && globalThis.self !== globalThis.top;
    if (isWorker) {
      this.#exposeWorkerAPI();
      globalThis[GUARD_PROP] = true;
    } else if (isIframe) {
      this.#exposeFrameAPI();
      globalThis[GUARD_PROP] = true;
    } else ;
  }
  #exposeWorkerAPI() {
    WorkerConsumerEndpoint.exposeAPI(new PlatformWorkerAPI());
  }
  #exposeFrameAPI() {
    IFrameConsumerEndpoint.exposeAPI(new PlatformFrameAPI());
  }
}

var EditorPlatformEnvironmentAPIErrorErrorCode = /* @__PURE__ */ ((EditorPlatformEnvironmentAPIErrorErrorCode2) => {
  EditorPlatformEnvironmentAPIErrorErrorCode2["ProxyEnvironmentConnectTimeout"] = "ProxyEnvironmentConnectTimeout";
  EditorPlatformEnvironmentAPIErrorErrorCode2["ProxyWaitAPITimeout"] = "ProxyWaitAPITimeout";
  EditorPlatformEnvironmentAPIErrorErrorCode2["ProxyContextsError"] = "ProxyContextsError";
  EditorPlatformEnvironmentAPIErrorErrorCode2["IncorrectExpose"] = "IncorrectExpose";
  return EditorPlatformEnvironmentAPIErrorErrorCode2;
})(EditorPlatformEnvironmentAPIErrorErrorCode || {});
class EditorPlatformEnvironmentAPIError extends BaseError {
  state = {};
  constructor(message, code) {
    super(message, code, "EP EnvironmentAPI Error");
  }
  withUrl(url) {
    this.state = { ...this.state, url };
    return this;
  }
  withAppDefinitionId(appDefinitionId) {
    this.state = { ...this.state, appDefinitionId };
    return this;
  }
}
const createEditorPlatformEnvironmentAPIError = createErrorBuilder(EditorPlatformEnvironmentAPIError);

class EditorPlatformEnvironmentProxy {
  async iframe() {
    throw createEditorPlatformInternalError(
      EditorPlatformInternalErrorCode.UnexpectedError,
      "not supported"
    );
  }
  async worker(worker) {
    const environment = new EditorPlatformContextEnvironment();
    if (!environment.isEnvironmentApiExposed()) {
      environment.expose();
    }
    const connectTimeoutId = setTimeout(() => {
      console.info(
        createEditorPlatformEnvironmentAPIError(
          EditorPlatformEnvironmentAPIErrorErrorCode.ProxyEnvironmentConnectTimeout,
          "probably worker environment does not expose platform API or any SDK function is never used"
        )
      );
    }, 2e3);
    WorkerHostEndpoint.connect(worker, async (transport) => {
      clearTimeout(connectTimeoutId);
      const waitAPITimeoutId = setTimeout(() => {
        console.error(
          createEditorPlatformEnvironmentAPIError(
            EditorPlatformEnvironmentAPIErrorErrorCode.ProxyWaitAPITimeout
          )
        );
      }, 2e3);
      const workerApi = await transport.waitAPI(
        PlatformConsumerEnvironmentAPIType.Worker
      );
      clearTimeout(waitAPITimeoutId);
      try {
        const contexts = await environment.getContexts();
        await workerApi.injectEnvironmentContext({
          events: contexts.environmentContext.getEvents(),
          privateApi: contexts.environmentContext.getPrivateAPI(),
          applicationAPIs: contexts.environmentContext.getApplicationAPIs()
        });
        await workerApi.injectApplicationContext(
          contexts.applicationContext.getContext()
        );
      } catch (e) {
        throw createEditorPlatformEnvironmentAPIError(
          EditorPlatformEnvironmentAPIErrorErrorCode.ProxyContextsError
        ).withParentError(e);
      }
    });
  }
}

export { EditorPlatformContextEnvironment, EditorPlatformEnvironmentProxy, PlatformConsumerEnvironmentAPIType, PlatformFrameAPI, PlatformWorkerAPI };
//# sourceMappingURL=index.js.map
